import { Injectable } from '@angular/core';
import {GasStationModel, GasStationsService as ApiGasStationsService, ResponseModel} from '@core/api';
import { HttpParams } from '@angular/common/http';
import { finalize, map, tap } from 'rxjs/operators';
import { Observable } from 'rxjs/internal/Observable';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';

@Injectable({
  providedIn: 'root'
})
export class StationService {

  private stationSubject = new BehaviorSubject<GasStationModel>(null);
  public station$ = this.stationSubject.asObservable();

  private stationLoadingSubject = new BehaviorSubject<boolean>(false);
  public stationLoading$ = this.stationLoadingSubject.asObservable();

  private locationFilterSubject = new BehaviorSubject<{cityStationIds: number[], returnLocationId: number | null}>({
    cityStationIds: [],
    returnLocationId: null
  });
  public locationFilter$ = this.locationFilterSubject.asObservable();

  constructor(private apiGasStationsService: ApiGasStationsService) { }

  set station (s: GasStationModel) {
    this.stationSubject.next(s);
  }

  get station (): GasStationModel {
    return this.stationSubject.value;
  }

  getStations() {
    this.stationLoadingSubject.next(true);

    return this.apiGasStationsService.gasStationGet(null)
      .pipe(finalize(() => this.stationLoadingSubject.next(false)))
      .pipe(map(res => res.data));
  }

  getNStations(amount: number) {
    // this.stationLoadingSubject.next(true);

    const httpParams = new HttpParams()
      .set('per_page', amount.toString());

    return this.apiGasStationsService.gasStationGet(httpParams)
        // .pipe(finalize(() => this.stationLoadingSubject.next(false)))
        .pipe(map(res => res.data));
  }

  getGasStationsWithTransitSpaces(network_id: number, trailer_id?: number): Observable<ResponseModel<GasStationModel[]>> {
    let params = new HttpParams()
        .set('available_transit_spaces', "1")
        .set('network_id', network_id.toString());

    if (trailer_id) {
      params = params.set('trailer_id', trailer_id.toString());
    }

    return  this.apiGasStationsService.gasStationGet(params);
  }

  getNetworkStations(network_id: number): Observable<GasStationModel[]> {
    return this.apiGasStationsService.gasNetworkStation(network_id)
      .pipe(map(res => res.data || []));
  }

  getStationByBounce(loc_w?: number, loc_e?: number, loc_n?: number, loc_s?: number): Observable<GasStationModel[]> {
    const httpParams = new HttpParams()
      .set('loc[w]', loc_w.toString())
      .set('loc[e]', loc_e.toString())
      .set('loc[n]', loc_n.toString())
      .set('loc[s]', loc_s.toString());

    return this.apiGasStationsService.gasStationGet(httpParams)
      .pipe(map(res => res.data));
  }

  getStationById(id: number): Observable<GasStationModel> {
    return this.apiGasStationsService.gasStationIdGet(id)
      .pipe(map(res => res.data));
  }

  getCurrentStationById(id: number): Observable<GasStationModel> {
    this.stationLoadingSubject.next(true);

    return this.apiGasStationsService.gasStationIdGet(id)
      .pipe(finalize(() => this.stationLoadingSubject.next(false)))
      .pipe(map(res => res.data))
      .pipe(tap(station => this.stationSubject.next(station)));
  }

  getBestTariff(id, hours) {
    return this.apiGasStationsService.gasNetworkIdTariffBestPost(hours, id);
  }

  setLocationFilter(cityStationIds: number[], returnLocationId: number | null) {
    this.locationFilterSubject.next({ cityStationIds, returnLocationId });
  }

  get locationFilter(): {cityStationIds: number[], returnLocationId: number | null} {
    return this.locationFilterSubject.value;
  }

  reset() {
    this.stationSubject.next(null);
  }
}
